import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  deleteWorkspaceEmailTemplate,
  getWorkspaceEmailTemplate,
  listWorkspaceEmailTemplates,
  previewWorkspaceEmailTemplate,
  upsertWorkspaceEmailTemplate,
} from '../services/workspaceSettingsService';
import type {
  EmailTemplateDetail,
  EmailTemplateListItem,
  EmailTemplatePreviewResult,
  WorkspaceEmailTemplatesState,
} from '../types/workspace.types';

const initialState: WorkspaceEmailTemplatesState = {
  list: [],
  listStatus: 'idle',
  listError: null,
  editor: {
    open: false,
    templateType: null,
    detail: null,
    detailStatus: 'idle',
    detailError: null,
    saveStatus: 'idle',
    saveError: null,
    previewResult: null,
    previewStatus: 'idle',
    previewError: null,
  },
  resetDialog: {
    open: false,
    templateType: null,
    deleteStatus: 'idle',
    deleteError: null,
  },
};

export const fetchWorkspaceEmailTemplates = createAsyncThunk(
  'settings/workspaceEmailTemplates/fetchList',
  async (tenantId: string) => {
    return await listWorkspaceEmailTemplates(tenantId);
  }
);

export const fetchWorkspaceEmailTemplateDetail = createAsyncThunk(
  'settings/workspaceEmailTemplates/fetchDetail',
  async ({
    tenantId,
    templateType,
  }: {
    tenantId: string;
    templateType: string;
  }) => {
    return await getWorkspaceEmailTemplate(tenantId, templateType);
  }
);

export const saveWorkspaceEmailTemplate = createAsyncThunk(
  'settings/workspaceEmailTemplates/save',
  async ({
    tenantId,
    templateType,
    body,
  }: {
    tenantId: string;
    templateType: string;
    body: string;
  }) => {
    return await upsertWorkspaceEmailTemplate(tenantId, templateType, body);
  }
);

export const previewWorkspaceEmailTemplateThunk = createAsyncThunk(
  'settings/workspaceEmailTemplates/preview',
  async ({ templateType, body }: { templateType: string; body: string }) => {
    return await previewWorkspaceEmailTemplate(templateType, body);
  }
);

export const deleteWorkspaceEmailTemplateThunk = createAsyncThunk(
  'settings/workspaceEmailTemplates/delete',
  async ({
    tenantId,
    templateType,
  }: {
    tenantId: string;
    templateType: string;
  }) => {
    await deleteWorkspaceEmailTemplate(tenantId, templateType);
    return { templateType };
  }
);

const workspaceEmailTemplatesSlice = createSlice({
  name: 'settingsWorkspaceEmailTemplates',
  initialState,
  reducers: {
    openTemplateEditor(state, action: PayloadAction<string>) {
      state.editor.open = true;
      state.editor.templateType = action.payload;
      state.editor.detail = null;
      state.editor.detailStatus = 'idle';
      state.editor.detailError = null;
      state.editor.saveStatus = 'idle';
      state.editor.saveError = null;
      state.editor.previewResult = null;
      state.editor.previewStatus = 'idle';
      state.editor.previewError = null;
    },
    closeTemplateEditor(state) {
      state.editor.open = false;
      state.editor.templateType = null;
      state.editor.detail = null;
      state.editor.detailStatus = 'idle';
      state.editor.detailError = null;
      state.editor.saveStatus = 'idle';
      state.editor.saveError = null;
      state.editor.previewResult = null;
      state.editor.previewStatus = 'idle';
      state.editor.previewError = null;
    },
    openResetTemplateDialog(state, action: PayloadAction<string>) {
      state.resetDialog.open = true;
      state.resetDialog.templateType = action.payload;
      state.resetDialog.deleteStatus = 'idle';
      state.resetDialog.deleteError = null;
    },
    closeResetTemplateDialog(state) {
      state.resetDialog.open = false;
      state.resetDialog.templateType = null;
      state.resetDialog.deleteStatus = 'idle';
      state.resetDialog.deleteError = null;
    },
    clearWorkspaceEmailTemplatesState(state) {
      state.list = [];
      state.listStatus = 'idle';
      state.listError = null;
      state.editor = { ...initialState.editor };
      state.resetDialog = { ...initialState.resetDialog };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceEmailTemplates.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(
        fetchWorkspaceEmailTemplates.fulfilled,
        (state, action: PayloadAction<EmailTemplateListItem[]>) => {
          state.listStatus = 'succeeded';
          state.list = action.payload;
        }
      )
      .addCase(fetchWorkspaceEmailTemplates.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError =
          action.error.message ?? 'Failed to load workspace email templates';
      })
      .addCase(fetchWorkspaceEmailTemplateDetail.pending, (state) => {
        state.editor.detailStatus = 'loading';
        state.editor.detailError = null;
      })
      .addCase(
        fetchWorkspaceEmailTemplateDetail.fulfilled,
        (state, action: PayloadAction<EmailTemplateDetail>) => {
          state.editor.detailStatus = 'succeeded';
          state.editor.detail = action.payload;
        }
      )
      .addCase(fetchWorkspaceEmailTemplateDetail.rejected, (state, action) => {
        state.editor.detailStatus = 'failed';
        state.editor.detailError =
          action.error.message ?? 'Failed to load template details';
      })
      .addCase(saveWorkspaceEmailTemplate.pending, (state) => {
        state.editor.saveStatus = 'loading';
        state.editor.saveError = null;
      })
      .addCase(
        saveWorkspaceEmailTemplate.fulfilled,
        (state, action: PayloadAction<EmailTemplateDetail>) => {
          state.editor.saveStatus = 'succeeded';
          state.editor.detail = action.payload;

          const idx = state.list.findIndex(
            (x) => x.templateType === action.payload.templateType
          );
          if (idx >= 0) {
            state.list[idx].source = 'TenantOverride';
            state.list[idx].hasOverride = true;
          }
        }
      )
      .addCase(saveWorkspaceEmailTemplate.rejected, (state, action) => {
        state.editor.saveStatus = 'failed';
        state.editor.saveError =
          action.error.message ?? 'Failed to save email template';
      })
      .addCase(previewWorkspaceEmailTemplateThunk.pending, (state) => {
        state.editor.previewStatus = 'loading';
        state.editor.previewError = null;
        state.editor.previewResult = null;
      })
      .addCase(
        previewWorkspaceEmailTemplateThunk.fulfilled,
        (state, action: PayloadAction<EmailTemplatePreviewResult>) => {
          state.editor.previewStatus = 'succeeded';
          state.editor.previewResult = action.payload;
        }
      )
      .addCase(previewWorkspaceEmailTemplateThunk.rejected, (state, action) => {
        state.editor.previewStatus = 'failed';
        state.editor.previewError =
          action.error.message ?? 'Failed to preview email template';
        state.editor.previewResult = null;
      })
      .addCase(deleteWorkspaceEmailTemplateThunk.pending, (state) => {
        state.resetDialog.deleteStatus = 'loading';
        state.resetDialog.deleteError = null;
      })
      .addCase(deleteWorkspaceEmailTemplateThunk.fulfilled, (state, action) => {
        state.resetDialog.deleteStatus = 'succeeded';

        const idx = state.list.findIndex(
          (x) => x.templateType === action.payload.templateType
        );
        if (idx >= 0) {
          state.list[idx].hasOverride = false;
        }
      })
      .addCase(deleteWorkspaceEmailTemplateThunk.rejected, (state, action) => {
        state.resetDialog.deleteStatus = 'failed';
        state.resetDialog.deleteError =
          action.error.message ?? 'Failed to reset email template';
      });
  },
});

export const {
  openTemplateEditor,
  closeTemplateEditor,
  openResetTemplateDialog,
  closeResetTemplateDialog,
  clearWorkspaceEmailTemplatesState,
} = workspaceEmailTemplatesSlice.actions;

export const workspaceEmailTemplatesReducer =
  workspaceEmailTemplatesSlice.reducer;
